import { Routes } from '@angular/router';
import { environment } from '../environments/environment';

import { CreatePasswordComponent } from './standalone/components/create-password/create-password.component';
import { HomeComponent } from './standalone/components/home/home.component';
import { SignInComponent } from './standalone/components/sign-in/sign-in.component';
import { moduleGuard } from './standalone/guards/module/module.guard';
import { SEOResolver } from './standalone/resolvers/seo.resolver';

/******************************************************************************************
 * Base Routes - No permissions required, eagerly loaded
 ******************************************************************************************/
export const routes: Routes = [
  {
    path: 'home',
    component: HomeComponent,
    resolve: { SEOResolver },
  },
  {
    path: 'register/:firestoreId',
    component: CreatePasswordComponent,
    data: {
      seoParams: {
        metaTitle: 'Create Password',
      },
    },
    resolve: { SEOResolver },
  },
  {
    // EXT-6599: the signed-in user's own profile. A base route like
    // my-feedback: every signed-in user has one, and the API scopes it to the
    // token.
    path: 'my-profile',
    data: {
      seoParams: {
        metaTitle: 'My Profile',
      },
    },
    resolve: { SEOResolver },
    loadComponent: () =>
      import('./standalone/components/my-profile/my-profile.component').then(
        (c) => c.MyProfileComponent,
      ),
  },
  {
    // EXT-6244: a submitter's own feedback. A base route, not a module route:
    // every signed-in user may send feedback, and no module grants it, so
    // moduleGuard would turn them away. The API scopes it to the token.
    path: 'my-feedback',
    data: {
      feedbackListMode: 'mine',
      seoParams: {
        metaTitle: 'My Feedback',
      },
    },
    resolve: { SEOResolver },
    loadComponent: () =>
      import('./standalone/components/platform-feedback/feedback-list/feedback-list.component').then(
        (c) => c.FeedbackListComponent,
      ),
  },
  {
    path: 'sign-in',
    component: SignInComponent,
    data: {
      seoParams: {
        metaTitle: `Sign-In`,
      },
    },
    resolve: {
      SEOResolver,
    },
  },
];

/******************************************************************************************
 * Module Routes - Permissions required, lazy loaded
 ******************************************************************************************/
const moduleRoutes: Routes = [
  {
    path: 'decrypt-tool',
    data: {
      seoParams: {
        metaTitle: `Decrypt Tool`,
      },
    },
    resolve: {
      SEOResolver,
    },
    loadComponent: () =>
      import('./standalone/components/crypto/crypto.component').then(
        (c) => c.CryptoComponent,
      ),
  },
  {
    path: 'encrypt-collections',
    data: {
      seoParams: {
        metaTitle: `Encrypt Collections`,
      },
    },
    resolve: {
      SEOResolver,
    },
    loadComponent: () =>
      import('./standalone/components/encrypt-collections/encrypt-collections.component').then(
        (c) => c.EncryptCollectionsComponent,
      ),
  },
  {
    path: 'cart-utils/stuck-cart-responses',
    data: {
      seoParams: {
        metaTitle: `IT Stuck Carts Responses`,
      },
    },
    resolve: {
      SEOResolver,
    },
    loadComponent: () =>
      import('./standalone/components/cart-utils/stuck-cart-responses/stuck-csrs.component').then(
        (c) => c.StuckCSRScomponent,
      ),
  },
  {
    path: 'cart-utils/stuck-carts-temp',
    data: {
      seoParams: {
        metaTitle: `IT Stuck Carts Temp`,
      },
    },
    resolve: {
      SEOResolver,
    },
    loadComponent: () =>
      import('./standalone/components/cart-utils/stuck-carts-temp/stuck-carts-temp.component').then(
        (c) => c.StuckCartsTempComponent,
      ),
  },
  {
    path: 'platform-user/invite',
    data: {
      seoParams: {
        metaTitle: `Platform User Invite`,
      },
    },
    resolve: {
      SEOResolver,
    },
    loadComponent: () =>
      import('./standalone/components/platform-user/invite-user/invite-user.component').then(
        (c) => c.InvitePlatformUserComponent,
      ),
  },
  {
    path: 'platform-user/list',
    data: {
      seoParams: {
        metaTitle: `Platform User List`,
      },
    },
    resolve: {
      SEOResolver,
    },
    loadComponent: () =>
      import('./standalone/components/platform-user/user-list/user-list.component').then(
        (c) => c.UserListComponent,
      ),
  },
  {
    path: 'platform-user/edit/:documentId',
    data: {
      seoParams: {
        metaTitle: `Platform User Edit`,
      },
    },
    resolve: {
      SEOResolver,
    },
    loadComponent: () =>
      import('./standalone/components/platform-user/edit-user/edit-user.component').then(
        (c) => c.EditPlatformUserComponent,
      ),
  },
  {
    path: 'geolocation/todo',
    data: {
      seoParams: {
        metaTitle: `Ecommerce To Do Account Details`,
      },
    },
    resolve: {
      SEOResolver,
    },
    loadComponent: () =>
      import('./standalone/components/geolocation/geolocation.component').then(
        (c) => c.GeolocationComponent,
      ),
  },
  {
    path: 'geolocation',
    data: {
      seoParams: {
        metaTitle: `Ecommerce Account Details`,
      },
    },
    resolve: {
      SEOResolver,
    },
    loadComponent: () =>
      import('./standalone/components/geolocation/geolocation.component').then(
        (c) => c.GeolocationComponent,
      ),
  },
  {
    path: 'groom-try-ons/view',
    data: {
      seoParams: {
        metaTitle: `Groom Try Ons View`,
      },
    },
    resolve: {
      SEOResolver,
    },
    loadComponent: () =>
      import('./standalone/components/groom-try-ons/groom-try-ons.component').then(
        (c) => c.GroomTryOnsComponent,
      ),
  },
  {
    path: 'groom-try-ons/manage',
    data: {
      seoParams: {
        metaTitle: `Groom Try Ons Manage`,
      },
    },
    resolve: {
      SEOResolver,
    },
    loadComponent: () =>
      import('./standalone/components/groom-try-ons/groom-try-ons.component').then(
        (c) => c.GroomTryOnsComponent,
      ),
  },
  {
    path: 'fashion-shows/view',
    data: {
      seoParams: {
        metaTitle: `Fashion Shows View`,
      },
    },
    resolve: {
      SEOResolver,
    },
    loadComponent: () =>
      import('./standalone/components/fashion-shows/fashion-shows.component').then(
        (c) => c.FashionShowsComponent,
      ),
  },
  {
    path: 'fashion-shows/manage',
    data: {
      seoParams: {
        metaTitle: `Fashion Shows Manage`,
      },
    },
    resolve: {
      SEOResolver,
    },
    loadComponent: () =>
      import('./standalone/components/fashion-shows/fashion-shows.component').then(
        (c) => c.FashionShowsComponent,
      ),
  },
  {
    path: 'essentials-on-order',
    data: {
      seoParams: {
        metaTitle: `Essentials On Order`,
      },
    },
    resolve: {
      SEOResolver,
    },
    loadComponent: () =>
      import('./standalone/components/essentials-on-order/essentials-on-order.component').then(
        (c) => c.EssentialsOnOrderComponent,
      ),
  },
  {
    path: 'ecommerce-orders',
    data: {
      seoParams: {
        metaTitle: `Ecommerce Orders`,
      },
    },
    resolve: {
      SEOResolver,
    },
    loadComponent: () =>
      import('./standalone/components/ecom-orders/ecom-orders.component').then(
        (c) => c.EcomOrdersComponent,
      ),
  },
  {
    path: 'transaction-tool/:eventId',
    data: {
      seoParams: {
        metaTitle: `Ecommerce Transaction Tool`,
      },
    },
    resolve: {
      SEOResolver,
    },
    loadComponent: () =>
      import('./standalone/components/transaction-tool/transaction-tool.component').then(
        (c) => c.TransactionToolComponent,
      ),
  },
  {
    path: 'event-search',
    data: {
      seoParams: {
        metaTitle: `Ecommerce Event Search`,
      },
    },
    resolve: {
      SEOResolver,
    },
    loadComponent: () =>
      import('./standalone/components/event-search/event-search.component').then(
        (c) => c.EventSearchComponent,
      ),
  },
  {
    path: 'event-search/:eventId',
    data: {
      seoParams: {
        metaTitle: `Ecommerce Event Details`,
      },
    },
    resolve: {
      SEOResolver,
    },
    loadComponent: () =>
      import('./standalone/components/event-detail/event-detail.component').then(
        (c) => c.EventDetailComponent,
      ),
  },
  {
    path: 'event-search/:eventId/co-owner',
    data: {
      seoParams: {
        metaTitle: `Ecommerce Co-Owner Edit`,
      },
    },
    resolve: {
      SEOResolver,
    },
    loadComponent: () =>
      import('./standalone/components/event-detail/update-co-owner/update-co-owner.component').then(
        (c) => c.EventUpdateCoOwnerComponent,
      ),
  },
  {
    path: 'event-search/:eventId/store-info',
    data: {
      seoParams: {
        metaTitle: `Ecommerce Store Info Edit`,
      },
    },
    resolve: {
      SEOResolver,
    },
    loadComponent: () =>
      import('./standalone/components/event-detail/update-store-info/update-store-info.component').then(
        (c) => c.EventUpdateStoreInfoComponent,
      ),
  },
  {
    path: 'non-accepted-in-store-events',
    data: {
      seoParams: {
        metaTitle: `Ecommerce Non-Accepted In Store`,
      },
    },
    resolve: {
      SEOResolver,
    },
    loadComponent: () =>
      import('./standalone/components/non-accepted-in-store-events/non-accepted-in-store-events.component').then(
        (c) => c.NonAcceptedInStoreEventsComponent,
      ),
  },
  {
    path: 'non-accepted-in-store-events/:eventId',
    data: {
      seoParams: {
        metaTitle: `Ecommerce Event Details`,
      },
    },
    resolve: {
      SEOResolver,
    },
    loadComponent: () =>
      import('./standalone/components/event-detail/event-detail.component').then(
        (c) => c.EventDetailComponent,
      ),
  },
  {
    path: 'non-accepted-in-store-events/:eventId/co-owner',
    data: {
      seoParams: {
        metaTitle: `Ecommerce Co-Owner Edit`,
      },
    },
    resolve: {
      SEOResolver,
    },
    loadComponent: () =>
      import('./standalone/components/event-detail/update-co-owner/update-co-owner.component').then(
        (c) => c.EventUpdateCoOwnerComponent,
      ),
  },
  {
    path: 'non-accepted-in-store-events/:eventId/store-info',
    data: {
      seoParams: {
        metaTitle: `Ecommerce Store Info Edit`,
      },
    },
    resolve: {
      SEOResolver,
    },
    loadComponent: () =>
      import('./standalone/components/event-detail/update-store-info/update-store-info.component').then(
        (c) => c.EventUpdateStoreInfoComponent,
      ),
  },
  {
    path: 'outstanding-ecom-orders',
    data: {
      seoParams: {
        metaTitle: `Ecommerce Outstanding Late Orders`,
      },
    },
    resolve: {
      SEOResolver,
    },
    loadComponent: () =>
      import('./standalone/components/outstanding-ecom-orders/outstanding-ecom-orders.component').then(
        (c) => c.OutstandingEcomOrdersComponent,
      ),
  },
  {
    path: 'ecom-promo-orders',
    data: {
      seoParams: {
        metaTitle: `Ecommerce Promo Orders`,
      },
    },
    resolve: {
      SEOResolver,
    },
    loadComponent: () =>
      import('./standalone/components/ecom-promo-orders/ecom-promo-orders.component').then(
        (c) => c.EcomPromoOrdersComponent,
      ),
  },
  {
    path: 'referral-dashboard',
    data: {
      seoParams: {
        metaTitle: `Referral Dashboard Report`,
      },
    },
    resolve: {
      SEOResolver,
    },
    loadComponent: () =>
      import('./standalone/components/referral-dashboard/referral-dashboard.component').then(
        (c) => c.ReferralDashboardComponent,
      ),
  },
  {
    path: 'commission/search',
    data: {
      seoParams: {
        metaTitle: `Commission Search`,
      },
    },
    resolve: {
      SEOResolver,
    },
    loadComponent: () =>
      import('./standalone/components/commission/search/commission-search.component').then(
        (c) => c.CommissionSearchComponent,
      ),
  },
  {
    path: 'commission/search/details/:orderId',
    data: {
      seoParams: {
        metaTitle: `Commission Details`,
      },
    },
    resolve: {
      SEOResolver,
    },
    loadComponent: () =>
      import('./standalone/components/commission/details/commission-details.component').then(
        (c) => c.CommissionDetailsComponent,
      ),
  },
  {
    path: 'commission/manage',
    data: {
      seoParams: {
        metaTitle: `Commission Manage Unpaid`,
      },
    },
    resolve: {
      SEOResolver,
    },
    loadComponent: () =>
      import('./standalone/components/commission/manage-unpaid/commission-manage-unpaid.component').then(
        (c) => c.CommissionManageUnpaidComponent,
      ),
  },
  {
    path: 'commission/manage/details/:orderId',
    data: {
      seoParams: {
        metaTitle: `Commission Details`,
      },
    },
    resolve: {
      SEOResolver,
    },
    loadComponent: () =>
      import('./standalone/components/commission/details/commission-details.component').then(
        (c) => c.CommissionDetailsComponent,
      ),
  },
  {
    path: 'commission/approval-successful',
    data: {
      seoParams: {
        metaTitle: `Commission Submit Success`,
      },
    },
    resolve: {
      SEOResolver,
    },
    loadComponent: () =>
      import('./standalone/components/commission/approve-success/approve-success.component').then(
        (c) => c.CommissionApproveSuccessComponent,
      ),
  },
  {
    path: 'commission/approved',
    data: {
      seoParams: {
        metaTitle: `Commission Pay Approved`,
      },
    },
    resolve: {
      SEOResolver,
    },
    loadComponent: () =>
      import('./standalone/components/commission/approved/commission-approved.component').then(
        (c) => c.CommissionApprovedComponent,
      ),
  },
  {
    path: 'commission/group/paid',
    data: {
      seoParams: {
        metaTitle: `Commission Paid Group Search`,
      },
    },
    resolve: {
      SEOResolver,
    },
    loadComponent: () =>
      import('./standalone/components/commission/paid-group-listing/paid-group-listing.component').then(
        (c) => c.PaidGroupListingComponent,
      ),
  },
  {
    path: 'commission/group/statement/:id',
    data: {
      seoParams: {
        metaTitle: `Commission Paid Statement`,
      },
    },
    resolve: {
      SEOResolver,
    },
    loadComponent: () =>
      import('./standalone/components/commission/paid-group-statement/paid-group-statement.component').then(
        (c) => c.PaidGroupStatementComponent,
      ),
  },
  {
    path: 'commission/submission-successful',
    data: {
      seoParams: {
        metaTitle: `Commission Approved Success`,
      },
    },
    resolve: {
      SEOResolver,
    },
    loadComponent: () =>
      import('./standalone/components/commission/submit-success/commission-submit-success.component').then(
        (c) => c.CommissionSubmitSuccessComponent,
      ),
  },
  {
    path: 'commission/group/detail/:commissionAccountNumber/:fromDate/:toDate',
    data: {
      seoParams: {
        metaTitle: `Commission Unpaid Group Details`,
      },
    },
    resolve: {
      SEOResolver,
    },
    loadComponent: () =>
      import('./standalone/components/commission/group-detail/commission-group-detail.component').then(
        (c) => c.CommissionGroupDetailComponent,
      ),
  },
  {
    path: 'order-detail/:orderId',
    data: {
      seoParams: {
        metaTitle: `Ecommerce Order Detail`,
      },
    },
    resolve: {
      SEOResolver,
    },
    loadComponent: () =>
      import('./standalone/components/order-detail/order-detail.component').then(
        (c) => c.OrderDetailComponent,
      ),
  },
  {
    path: 'shoe-bagging',
    data: {
      seoParams: {
        metaTitle: `Shoe Bagging`,
      },
    },
    resolve: {
      SEOResolver,
    },
    loadComponent: () =>
      import('./standalone/components/production/shoe-bagging/shoe-bagging.component').then(
        (c) => c.ShoeBaggingComponent,
      ),
  },
  {
    path: 'ensembles/create',
    data: {
      seoParams: {
        metaTitle: `Ensembles Create`,
      },
    },
    resolve: {
      SEOResolver,
    },
    loadComponent: () =>
      import('./standalone/components/ensembles/create/create.component').then(
        (c) => c.CreateEnsembleComponent,
      ),
  },
  {
    path: 'ensembles/manage',
    data: {
      seoParams: {
        metaTitle: `Ensembles Management`,
      },
    },
    resolve: {
      SEOResolver,
    },
    loadComponent: () =>
      import('./standalone/components/ensembles/manage/manage.component').then(
        (c) => c.ManageEnsemblesComponent,
      ),
  },
  {
    path: 'ensembles/edit/:ensembleCode',
    data: {
      seoParams: {
        metaTitle: `Ensembles Edit`,
      },
    },
    resolve: {
      SEOResolver,
    },
    loadComponent: () =>
      import('./standalone/components/ensembles/edit/edit.component').then(
        (c) => c.EditEnsembleComponent,
      ),
  },
  {
    path: 'styles/manage',
    data: {
      seoParams: {
        metaTitle: 'Manage Styles',
      },
    },
    resolve: {
      SEOResolver,
    },
    loadComponent: () =>
      import('./standalone/components/style/manage/manage.component').then(
        (c) => c.ManageStylesComponent,
      ),
  },
  {
    path: 'style-catalog',
    data: {
      seoParams: {
        metaTitle: 'Style Catalog',
      },
    },
    resolve: {
      SEOResolver,
    },
    loadComponent: () =>
      import(
        './standalone/components/style-catalog/style-catalog.component'
      ).then((c) => c.StyleCatalogComponent),
  },
  {
    path: 'merchandise/bulk-price-update',
    data: {
      seoParams: {
        metaTitle: 'Bulk Merch Cost Change',
      },
    },
    resolve: {
      SEOResolver,
    },
    loadComponent: () =>
      import(
        './standalone/components/merchandise/bulk-price-update/bulk-price-update.component'
      ).then((c) => c.BulkPriceUpdateComponent),
  },
  {
    path: 'styles/create',
    data: {
      seoParams: {
        metaTitle: 'Create Style',
      },
    },
    resolve: {
      SEOResolver,
    },
    loadComponent: () =>
      import('./standalone/components/style/create/create.component').then(
        (c) => c.CreateStyleComponent,
      ),
  },
  {
    path: 'styles/edit/:styleCode',
    data: {
      seoParams: {
        metaTitle: 'Edit Style',
      },
    },
    resolve: {
      SEOResolver,
    },
    loadComponent: () =>
      import('./standalone/components/style/edit/edit.component').then(
        (c) => c.EditStyleComponent,
      ),
  },
  {
    path: 'style-groupings/create',
    data: {
      seoParams: {
        metaTitle: `Style Groupings Create`,
      },
    },
    resolve: {
      SEOResolver,
    },
    loadComponent: () =>
      import('./standalone/components/style-grouping/create/create.component').then(
        (c) => c.CreateStyleGroupingComponent,
      ),
  },
  {
    path: 'style-groupings/manage',
    data: {
      seoParams: {
        metaTitle: `Style Groupings Manage`,
      },
    },
    resolve: {
      SEOResolver,
    },
    loadComponent: () =>
      import('./standalone/components/style-grouping/manage/manage.component').then(
        (c) => c.ManageStyleGroupingsComponent,
      ),
  },
  {
    path: 'style-groupings/edit/:firestoreDocumentId',
    data: {
      seoParams: {
        metaTitle: `Style Groupings Edit`,
      },
    },
    resolve: {
      SEOResolver,
    },
    loadComponent: () =>
      import('./standalone/components/style-grouping/edit/edit.component').then(
        (c) => c.EditStyleGroupingComponent,
      ),
  },
  {
    path: 'colors/manage',
    data: {
      seoParams: {
        metaTitle: `Manage Colors`,
      },
    },
    resolve: {
      SEOResolver,
    },
    loadComponent: () =>
      import('./standalone/components/color/manage/manage.component').then(
        (c) => c.ManageColorsComponent,
      ),
  },
  {
    path: 'ecom-event-actions',
    data: {
      seoParams: {
        metaTitle: `update EventSearch Actions`,
      },
    },
    resolve: {
      SEOResolver,
    },
    loadComponent: () =>
      import('./standalone/components/ecom-event-actions/ecom-event-actions.component').then(
        (c) => c.EcomEventActionsComponent,
      ),
  },
  {
    path: 'dealer-user/create',
    data: {
      seoParams: {
        metaTitle: `Dealer User Create`,
      },
    },
    resolve: {
      SEOResolver,
    },
    loadComponent: () =>
      import('./standalone/components/dealer-user/create-user/create-user.component').then(
        (c) => c.CreateDealerUserComponent,
      ),
  },
  {
    path: 'dealer-user/edit/:documentId',
    data: {
      seoParams: {
        metaTitle: `Dealer User Edit`,
      },
    },
    resolve: {
      SEOResolver,
    },
    loadComponent: () =>
      import('./standalone/components/dealer-user/edit-user/edit-user.component').then(
        (c) => c.EditDealerUserComponent,
      ),
  },
  {
    path: 'dealer-user/list',
    data: {
      seoParams: {
        metaTitle: `Dealer User List`,
      },
    },
    resolve: {
      SEOResolver,
    },
    loadComponent: () =>
      import('./standalone/components/dealer-user/users/users.component').then(
        (c) => c.DealerUsersComponent,
      ),
  },
  {
    path: 'dealer-user/pending',
    data: {
      seoParams: {
        metaTitle: `Dealer User Pending`,
      },
    },
    resolve: {
      SEOResolver,
    },
    loadComponent: () =>
      import('./standalone/components/dealer-user/pending-users/pending-users.component').then(
        (c) => c.PendingDealerUsersComponent,
      ),
  },
  {
    path: 'looker-poc',
    loadComponent: () =>
      import('./standalone/components/looker-poc/looker-poc.component').then(
        (c) => c.LookerPocComponent,
      ),
    data: {
      seoParams: {
        metaTitle: 'Looker POC',
      },
    },
    resolve: { SEOResolver },
  },
  {
    path: 'dealer-portal-banners/create',
    data: {
      seoParams: {
        metaTitle: 'Create Dealer Portal Banner',
      },
    },
    resolve: {
      SEOResolver,
    },
    loadComponent: () =>
      import('./standalone/components/dealer-portal-banner/create/create.component').then(
        (c) => c.CreateDealerPortalBannerComponent,
      ),
  },
  {
    path: 'dealer-portal-banners/view',
    data: {
      seoParams: {
        metaTitle: 'View Dealer Portal Banners',
      },
    },
    resolve: {
      SEOResolver,
    },
    loadComponent: () =>
      import('./standalone/components/dealer-portal-banner/view/view.component').then(
        (c) => c.ViewDealerPortalBannersComponent,
      ),
  },
  {
    path: 'dealer-portal-banners/edit/:documentId',
    data: {
      seoParams: {
        metaTitle: 'Edit Dealer Portal Banner',
      },
    },
    resolve: {
      SEOResolver,
    },
    loadComponent: () =>
      import('./standalone/components/dealer-portal-banner/edit/edit.component').then(
        (c) => c.EditDealerPortalBannerComponent,
      ),
  },
  {
    path: 'product-image-uploader',
    data: {
      seoParams: {
        metaTitle: 'Product Image Uploader',
      },
    },
    resolve: {
      SEOResolver,
    },
    loadComponent: () =>
      import('./standalone/components/product-image-uploader/product-image-uploader.component').then(
        (c) => c.ProductImageUploaderComponent,
      ),
  },
  {
    path: 'sitemaps',
    data: {
      seoParams: {
        metaTitle: 'Sitemap Generator',
      },
    },
    resolve: {
      SEOResolver,
    },
    loadComponent: () =>
      import('./standalone/components/sitemaps/sitemaps.component').then(
        (c) => c.SitemapsComponent,
      ),
  },
  {
    path: 'elastic-reindex',
    data: {
      seoParams: {
        metaTitle: 'Elastic Reindex',
      },
    },
    resolve: {
      SEOResolver,
    },
    loadComponent: () =>
      import('./standalone/components/elastic-reindex/elastic-reindex.component').then(
        (c) => c.ElasticReindexComponent,
      ),
  },
  {
    // EXT-6537: Data Pods is now the Dashboard. /wholesale/data-pods
    // redirects here (see the end of this file), keeping its query params, so
    // the Dialpad screen pop's ?actNum= links and old bookmarks still work.
    path: 'wholesale/dashboard',
    data: {
      seoParams: {
        metaTitle: 'Dashboard',
      },
    },
    resolve: {
      SEOResolver,
    },
    loadComponent: () =>
      import('./standalone/components/wholesale/data-pods/data-pods.component').then(
        (c) => c.WholesaleDataPodsPageComponent,
      ),
  },
  {
    path: 'wholesale/event-planner',
    data: {
      seoParams: {
        metaTitle: 'Wholesale Event Planner',
      },
    },
    resolve: {
      SEOResolver,
    },
    loadComponent: () =>
      import('./standalone/components/wholesale/event-planner/event-planner.component').then(
        (c) => c.WholesaleEventPlannerPageComponent,
      ),
  },
  {
    path: 'wholesale/order-entry',
    data: {
      seoParams: {
        metaTitle: 'Wholesale Order Entry',
      },
    },
    resolve: {
      SEOResolver,
    },
    loadComponent: () =>
      import('./standalone/components/wholesale/order-entry/order-entry.component').then(
        (c) => c.WholesaleOrderEntryPageComponent,
      ),
  },
  {
    path: 'wholesale/receipt-viewer',
    data: {
      seoParams: {
        metaTitle: 'Receipt',
      },
    },
    resolve: {
      SEOResolver,
    },
    loadComponent: () =>
      import('./standalone/components/wholesale/receipt-viewer/receipt-viewer.component').then(
        (c) => c.WholesaleReceiptViewerPageComponent,
      ),
  },
  {
    path: 'wholesale/search',
    data: {
      seoParams: {
        metaTitle: 'Search',
      },
      // EXT-6013: keeps this route's component tree (filters + results,
      // both tabs) alive across navigate-away/back instead of destroying it
      // -- see ReuseComponentRouteStrategy.
      reuseComponent: true,
    },
    resolve: {
      SEOResolver,
    },
    loadComponent: () =>
      import('./standalone/components/wholesale/search/search.component').then(
        (c) => c.WholesaleSearchPageComponent,
      ),
  },
  {
    path: 'wholesale/pricing-inquiry',
    data: {
      seoParams: {
        metaTitle: 'Wholesale Pricing Inquiry',
      },
    },
    resolve: {
      SEOResolver,
    },
    loadComponent: () =>
      import('./standalone/components/wholesale/pricing-inquiry/pricing-inquiry.component').then(
        (c) => c.WholesalePricingInquiryPageComponent,
      ),
  },
  {
    path: 'wholesale/shipping-inquiry',
    data: {
      seoParams: {
        metaTitle: 'Shipping Inquiry',
      },
    },
    resolve: {
      SEOResolver,
    },
    loadComponent: () =>
      import('./standalone/components/wholesale/shipping-inquiry/shipping-inquiry.component').then(
        (c) => c.WholesaleShippingInquiryPageComponent,
      ),
  },
  {
    path: 'wholesale/pack-slips',
    data: {
      seoParams: {
        metaTitle: 'Wholesale Packing Slips',
      },
    },
    resolve: {
      SEOResolver,
    },
    loadComponent: () =>
      import('./standalone/components/wholesale/pack-slips/pack-slips.component').then(
        (c) => c.WholesalePackSlipsPageComponent,
      ),
  },
  {
    path: 'wholesale/billing-history',
    data: {
      seoParams: {
        metaTitle: 'Wholesale Billing History',
      },
    },
    resolve: {
      SEOResolver,
    },
    loadComponent: () =>
      import(
        './standalone/components/wholesale/billing-history/billing-history.component'
      ).then((c) => c.WholesaleBillingHistoryPageComponent),
  },
  {
    path: 'wholesale/order-history',
    data: {
      seoParams: {
        metaTitle: 'Order History',
      },
    },
    resolve: {
      SEOResolver,
    },
    loadComponent: () =>
      import(
        './standalone/components/wholesale/order-history/order-history.component'
      ).then((c) => c.WholesaleOrderHistoryPageComponent),
  },
  {
    path: 'wholesale/account-balance',
    data: {
      seoParams: {
        metaTitle: 'Account Balance',
      },
    },
    resolve: {
      SEOResolver,
    },
    loadComponent: () =>
      import(
        './standalone/components/wholesale/account-balance/account-balance.component'
      ).then((c) => c.WholesaleAccountBalancePageComponent),
  },
  {
    path: 'wholesale/low-inventory',
    data: {
      seoParams: {
        metaTitle: 'Low Inventory',
      },
    },
    resolve: {
      SEOResolver,
    },
    loadComponent: () =>
      import(
        './standalone/components/wholesale/low-inventory/low-inventory.component'
      ).then((c) => c.WholesaleLowInventoryPageComponent),
  },
  {
    path: 'wholesale/phone-account-association',
    data: {
      seoParams: {
        metaTitle: 'Phone/Account Associations',
      },
    },
    resolve: {
      SEOResolver,
    },
    loadComponent: () =>
      import(
        './standalone/components/wholesale/phone-account-association/phone-account-association.component'
      ).then((c) => c.PhoneAccountAssociationPageComponent),
  },
  {
    path: 'inspection',
    data: {
      seoParams: {
        metaTitle: 'Inspection',
      },
    },
    resolve: {
      SEOResolver,
    },
    loadComponent: () =>
      import('./standalone/components/production/inspection/inspection.component').then(
        (c) => c.InspectionComponent,
      ),
  },
  {
    path: 'create-blog',
    data: {
      seoParams: {
        metaTitle: 'Blogs',
      },
    },
    resolve: {
      SEOResolver,
    },
    loadComponent: () =>
      import('./standalone/components/blog/create-blogs/create-blog.component').then(
        (c) => c.CreateBlogComponent,
      ),
  },
  {
    path: 'manage-blog',
    data: {
      seoParams: {
        metaTitle: 'Blogs',
      },
    },
    resolve: {
      SEOResolver,
    },
    loadComponent: () =>
      import('./standalone/components/blog/manage-blogs/manage-blog.component').then(
        (c) => c.ManageBlogComponent,
      ),
  },
  {
    path: 'blog-image-uploader',
    data: {
      seoParams: {
        metaTitle: 'Blogs',
      },
    },
    resolve: {
      SEOResolver,
    },
    loadComponent: () =>
      import('./standalone/components/blog/blog-image-uploader/blog-image-uploader.component').then(
        (c) => c.BlogImageUploaderComponent,
      ),
  },
  {
    path: 'manage-announcements',
    data: {
      seoParams: {
        metaTitle: 'Announcements',
      },
    },
    resolve: {
      SEOResolver,
    },
    loadComponent: () =>
      import('./standalone/components/announcements/manage-announcements.component').then(
        (c) => c.ManageAnnouncementsComponent,
      ),
  },
  {
    path: 'platform-feedback/list',
    data: {
      seoParams: {
        metaTitle: 'Platform Portal Feedback',
      },
    },
    resolve: {
      SEOResolver,
    },
    loadComponent: () =>
      import('./standalone/components/platform-feedback/feedback-list/feedback-list.component').then(
        (c) => c.FeedbackListComponent,
      ),
  },
  {
    path: 'platform-feedback/view/:id',
    data: {
      seoParams: {
        metaTitle: 'Platform Portal Feedback Detail',
      },
    },
    resolve: {
      SEOResolver,
    },
    loadComponent: () =>
      import('./standalone/components/platform-feedback/feedback-view/feedback-view.component').then(
        (c) => c.FeedbackViewComponent,
      ),
  },
];

if (environment.envVersion === 'local' || environment.envVersion === 'dev') {
  // EXT-6566: the account maintenance screen over the new account model. Its
  // module is devBuildOnly, so test and prod neither route to nor list it.
  moduleRoutes.push(
    {
      path: 'account-maintenance',
      data: { seoParams: { metaTitle: 'Account Maintenance' } },
      resolve: { SEOResolver },
      loadComponent: () =>
        import('./standalone/components/account-maintenance/account-maintenance-search/account-maintenance-search.component').then(
          (c) => c.AccountMaintenanceSearchComponent,
        ),
    },
    {
      path: 'account-maintenance/:accountId',
      data: { seoParams: { metaTitle: 'Account Maintenance' } },
      resolve: { SEOResolver },
      loadComponent: () =>
        import('./standalone/components/account-maintenance/account-maintenance-detail/account-maintenance-detail.component').then(
          (c) => c.AccountMaintenanceDetailComponent,
        ),
    },
  );
  moduleRoutes.push({
    path: 'analyst-ai-agent',
    data: {
      seoParams: {
        metaTitle: `IT Analyst AI Agent`,
      },
    },
    resolve: {
      SEOResolver,
    },
    loadComponent: () =>
      import('./standalone/components/events-ai-agent-poc/analyst-ai-agent.component').then(
        (c) => c.AnalystAiAgentPocComponent,
      ),
  });
}

for (const route of moduleRoutes) {
  route.canActivate = [moduleGuard];
  routes.push(route);
}

// EXT-6537: kept outside moduleRoutes because a redirect can't carry a guard;
// the Dashboard route it lands on is guarded.
routes.push({ path: 'wholesale/data-pods', redirectTo: 'wholesale/dashboard' });

routes.push({ path: '**', redirectTo: '/home' });
